import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { CoverageImpactMessage } from 'partner360/features/topPartnersManagement/components/coverageImpactMessage/CoverageImpactMessage'
import { INCLUDE_MARKET_FLAGS } from 'partner360/features/topPartnersManagement/configs'
import { getMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/getMarketCoverage'
import { shouldUseFlagRequest } from 'partner360/features/topPartnersManagement/helpers/shouldUseFlagRequest'
import { sumMarketCoverage } from 'partner360/features/topPartnersManagement/helpers/sumMarketCoverage'
import { useFlagRequestActions } from 'partner360/features/topPartnersManagement/hooks/useFlagRequestActions'
import { IMarketCoverageImpact } from 'partner360/features/topPartnersManagement/models'
import { TRANSLATION_KEYS } from 'partner360/features/topPartnersManagement/translations'
import { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'
import { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'
import { TFlagChangeRequestStatus } from 'partner360/models/partnerFlagRequests/TFlagChangeRequestStatus'

import { hasFlag } from '../../../helpers/hasFlag'

import { TopPartnerEditorMainInfo } from './components/topPartnerEditorMainInfo/TopPartnerEditorMainInfo'

import styles from './TopPartnerEditorContent.module.scss'

interface ITopPartnerEditorProps {
  partnerId?: string
  partnerName: string
  bitClass?: number
  topFlags: IP360PartnerFlags[]
  partnerFlags: IP360PartnerFlags[]
  onClose: () => void
  onSavingChange: (saving: boolean) => void
  isSaving: boolean
  fYear: number
  partnersNotInTable: IPartnerWithFlagRequests[]
  allPartners: IPartnerWithFlagRequests[]
  item: IPartnerWithFlagRequests
  setPartners: Dispatch<SetStateAction<IPartnerWithFlagRequests[]>>
  approvers: string[]
}

export const TopPartnerEditorContent = ({
  partnerName,
  partnerId,
  bitClass = 0,
  topFlags,
  partnerFlags,
  onClose,
  onSavingChange,
  isSaving,
  fYear,
  partnersNotInTable,
  allPartners,
  item,
  setPartners,
  approvers,
}: ITopPartnerEditorProps) => {
  const batchSavingRef = useRef(false)
  const isAddMode = !partnerName

  const [selectedBitClass, setSelectedBitClass] = useState(bitClass)
  const [selectedItem, setSelectedItem] = useState(item)
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>([])
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<string[]>([])

  const handleCountryCodesChange = useCallback((countryCodes: string[]) => {
    setSelectedCountryCodes(countryCodes)
    setSelectedPartnerIds([])
  }, [])

  const { isLoading, isApprover, createRequest, updateRequestStatus } = useFlagRequestActions({
    fYear,
    approvers,
    setPartners,
  })

  const preparedFlags = useMemo(() => {
    const parsedRequest = selectedItem.Request ? parseJson(selectedItem.Request) : null

    return topFlags.map((flag) => {
      const isAppliedInBitClass = Boolean(flag.BitValue && hasFlag(selectedBitClass, flag.BitValue))

      const requestValue = flag.FlagKey ? parsedRequest?.[flag.FlagKey] : undefined

      const shouldUseRequest = shouldUseFlagRequest(selectedItem.RequestStatus)

      let checked = isAppliedInBitClass

      if (shouldUseRequest && requestValue !== undefined) {
        checked = requestValue === 1
      }

      return {
        ...flag,
        checked,
      }
    })
  }, [selectedBitClass, topFlags, selectedItem])

  const handleToggleFlag = useCallback((bitValue: number) => {
    setSelectedBitClass((prev) => {
      const isChecked = (prev & bitValue) === bitValue

      if (isChecked) {
        return prev & ~bitValue
      }

      return prev | bitValue
    })
  }, [])

  const onChangeRequestStatus = useCallback(
    (status: TFlagChangeRequestStatus) => async () => {
      if (!selectedItem.RequestId) {
        return
      }

      const res = await updateRequestStatus(selectedItem.RequestId, status)
      if (!res) return

      setSelectedItem((prev) => ({
        ...prev,
        RequestCreated: res.RequestCreated,
        RequestCreatedBy: res.RequestCreatedBy,
        RequestStatus: res.RequestStatus,
        BitClass: res.BitClass,
        RequestId: res.RequestId,
      }))

      if (status !== 'Deleted') {
        onClose()
      }
    },
    [updateRequestStatus, onClose, selectedItem.RequestId]
  )

  const onSave = useCallback(async () => {
    if (batchSavingRef.current) return
    if (isAddMode) {
      if (!selectedPartnerIds.length) {
        return
      }

      const request = !INCLUDE_MARKET_FLAGS
        ? topFlags[0]?.FlagKey
          ? { [topFlags[0].FlagKey]: 1 as const }
          : null
        : topFlags.reduce<Record<string, 0 | 1>>((acc, flag) => {
            if (flag?.FlagKey && hasFlag(selectedBitClass, flag.BitValue)) {
              acc[flag.FlagKey] = 1
            }
            return acc
          }, {})

      if (!request || !Object.keys(request).length) {
        return
      }

      batchSavingRef.current = true
      onSavingChange(true)
      try {
        for (const partnerIdToAdd of selectedPartnerIds) {
          const res = await createRequest(partnerIdToAdd, request)
          if (!res) return
          setSelectedPartnerIds((ids) => ids.filter((id) => id !== partnerIdToAdd))
        }
      } finally {
        batchSavingRef.current = false
        onSavingChange(false)
      }

      onClose()
      return
    }

    if (!partnerId) {
      return
    }

    const request = topFlags.reduce<Record<string, 0 | 1>>((acc, flag) => {
      const wasChecked = hasFlag(bitClass, flag.BitValue)
      const isChecked = hasFlag(selectedBitClass, flag.BitValue)

      if (wasChecked === isChecked) {
        return acc
      }

      if (flag?.FlagKey) {
        acc[flag?.FlagKey] = isChecked ? 1 : 0
      }

      return acc
    }, {})

    const res = await createRequest(partnerId, request)
    if (res) onClose()
  }, [
    isAddMode,
    onSavingChange,
    selectedPartnerIds,
    topFlags,
    selectedBitClass,
    createRequest,
    onClose,
    partnerId,
    bitClass,
  ])

  const isSaveDisabled = isAddMode
    ? !selectedPartnerIds.length || (INCLUDE_MARKET_FLAGS && !selectedBitClass)
    : !partnerId ||
      selectedBitClass === selectedItem.BitClass ||
      (!selectedItem.PartnerId && !selectedBitClass)

  const isChangeable = useMemo(() => {
    return !selectedItem.RequestStatus || !shouldUseFlagRequest(selectedItem.RequestStatus)
  }, [selectedItem.RequestStatus])

  const coverageFlag = useMemo(() => {
    if (!INCLUDE_MARKET_FLAGS) {
      return topFlags[0]
    }

    return topFlags.find(
      (flag) => Boolean(flag.BitValue) && hasFlag(selectedBitClass, flag.BitValue)
    )
  }, [topFlags, selectedBitClass])

  const addCoverageImpacts = useMemo<IMarketCoverageImpact[]>(() => {
    if (!isAddMode || !coverageFlag || !selectedPartnerIds.length) {
      return []
    }

    const selectedIdSet = new Set(selectedPartnerIds)
    const selectedPartners = partnersNotInTable.filter((partner) =>
      selectedIdSet.has(partner.PartnerId)
    )

    const partnersByMarket = new Map<string, IPartnerWithFlagRequests[]>()
    selectedPartners.forEach((partner) => {
      if (!partner.GeoMarket) {
        return
      }
      const group = partnersByMarket.get(partner.GeoMarket) ?? []
      group.push(partner)
      partnersByMarket.set(partner.GeoMarket, group)
    })

    return Array.from(partnersByMarket.entries()).map(([geoMarket, newPartners]) => {
      const marketPartners = allPartners.filter((partner) => partner.GeoMarket === geoMarket)
      const current = sumMarketCoverage(marketPartners, coverageFlag) ?? 0
      const added = newPartners.reduce(
        (sum, partner) => sum + (getMarketCoverage(partner.Coverage, coverageFlag) ?? 0),
        0
      )

      return { geoMarket, current, next: current + added }
    })
  }, [isAddMode, coverageFlag, selectedPartnerIds, partnersNotInTable, allPartners])

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading || isSaving} />

      <div className={styles.header}>
        <div className={styles.title}>
          {isAddMode && !INCLUDE_MARKET_FLAGS && topFlags[0] ? (
            <Translation
              span
              text={TRANSLATION_KEYS.topPartners.editModal.addFlagPartnerTitle}
              replaceParams={[{ key: '{flagName}', value: topFlags[0].Title }]}
            />
          ) : (
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.title} />
          )}
        </div>
      </div>

      <TopPartnerEditorMainInfo
        item={selectedItem}
        partnerId={partnerId}
        partnerName={partnerName}
        topFlags={topFlags}
        selectedCountryCodes={selectedCountryCodes}
        onCountryCodesChange={handleCountryCodesChange}
        selectedPartnerIds={selectedPartnerIds}
        onPartnerIdsChange={setSelectedPartnerIds}
        preparedFlags={preparedFlags}
        isChangeable={isChangeable}
        handleToggleFlag={handleToggleFlag}
        partnersNotInTable={partnersNotInTable}
        partnerFlags={partnerFlags}
      />

      {addCoverageImpacts.map((impact) => (
        <CoverageImpactMessage
          key={impact.geoMarket}
          coverageImpact={impact}
          translationKey={TRANSLATION_KEYS.topPartners.editModal.coverageImpact}
          tone="increase"
          extraReplaceParams={[{ key: '{market}', value: impact.geoMarket }]}
        />
      ))}

      <div className={styles.footer}>
        <RenderWhen
          condition={
            !selectedItem.RequestStatus || !shouldUseFlagRequest(selectedItem.RequestStatus)
          }
        >
          <Button disabled={isSaveDisabled || isSaving} onClick={onSave}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.save} />
          </Button>
        </RenderWhen>

        <RenderWhen condition={selectedItem.RequestStatus === 'New'}>
          <Button color={'danger'} disabled={!partnerId} onClick={onChangeRequestStatus('Deleted')}>
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.remove} />
          </Button>
        </RenderWhen>

        <RenderWhen condition={selectedItem.RequestStatus === 'Wip' && isApprover}>
          <Button
            color={'success'}
            disabled={!partnerId}
            onClick={onChangeRequestStatus('Approved')}
          >
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.approve} />
          </Button>

          <Button
            color={'danger'}
            disabled={!partnerId}
            onClick={onChangeRequestStatus('Rejected')}
          >
            <Translation span text={TRANSLATION_KEYS.topPartners.editModal.reject} />
          </Button>
        </RenderWhen>

        <Button color={'grey'} disabled={isSaving} onClick={onClose}>
          <Translation span text={TRANSLATION_KEYS.topPartners.editModal.cancel} />
        </Button>
      </div>
    </div>
  )
}
